import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { getAssetHierarchyPalette, getContainedButtonSx, getDialogSectionBoxSx, getOutlinedButtonSx } from "../assetHierarchyStyles";
import { getAssetDocumentTypeOptions } from "../assetDocumentEnums";
import { updateAssetDocument } from "../../../fetchers/cmmsAssetDocument";
import cmmsMessages from "../cmmsMessages";
import messages from "../messages";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "./CmmsStates";

export default function AssetDocumentEditDialog({ assetDocument, assetNodeId, companyKey, onClose, open }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const queryClient = useQueryClient();

  const [formState, setFormState] = useState({ documentType: "", title: "", description: "" });

  const typeOptions = useMemo(() => getAssetDocumentTypeOptions(intl), [intl]);

  useEffect(() => {
    if (open && assetDocument) {
      setFormState({
        documentType: assetDocument.documentType,
        title: assetDocument.title ?? "",
        description: assetDocument.description ?? "",
      });
    }
  }, [open, assetDocument]);

  const updateMutation = useMutation({
    mutationFn: () => updateAssetDocument(assetNodeId, assetDocument.id, {
      companyKey,
      documentType: formState.documentType,
      title: formState.title,
      description: formState.description,
    }),
  });

  const handleChange = (event) => {
    const { name, id, value } = event.target;
    setFormState((current) => ({ ...current, [name || id]: value }));
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync();
      await queryClient.invalidateQueries(["cmmsAssetDocuments", companyKey, assetNodeId]);
      pushNotification(intl.formatMessage(messages.editDocumentUpdated));
      onClose();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.editDocumentFailed)));
    }
  };

  const isSaving = updateMutation.isPending || updateMutation.isLoading;
  const canSave = formState.title.trim().length > 0 && !isSaving;

  return (
    <Popup openPopup={open} setOpenPopup={onClose} maxWidth="sm" title={intl.formatMessage(messages.editDocumentTitle)}>
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box sx={getDialogSectionBoxSx(cmmsColors)}>
          <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14 }}>
            <FormattedMessage {...messages.editDocumentIntro} />
          </Typography>
          {assetDocument &&
            <Typography variant="caption" sx={{ color: cmmsColors.mutedText, display: "block", mt: 1, wordBreak: "break-all" }}>
              <FormattedMessage {...messages.editDocumentFileLine} values={{ fileName: assetDocument.fileName }} />
            </Typography>
          }
        </Box>

        <Box sx={{ display: "grid", gap: 2 }}>
          <AxionControls.AxionSelect
            name="documentType"
            label={intl.formatMessage(messages.fieldType)}
            value={formState.documentType}
            onChange={handleChange}
            options={typeOptions}
            showNoneOption={false}
          />
          <AxionControls.AxionTextBox
            name="title"
            label={intl.formatMessage(messages.fieldTitle)}
            value={formState.title}
            onChange={handleChange}
          />
          <AxionControls.AxionMultiLineText
            name="description"
            label={intl.formatMessage(messages.fieldDescription)}
            value={formState.description}
            rows={3}
            onChange={handleChange}
          />
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSaving} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.save} />
          </Button>
        </Stack>
      </Box>
    </Popup>
  );
}
